// Cloud-admin shared UI: KPI cards, status pill, a global toast queue, and a
// typed-key destructive-confirm modal. Mirrors the operator app's primitives
// (same IOSentra glass `tk-*` classes) so 5174 and 5173 look identical, without a
// cross-app import (cloud-admin's @/* only resolves into its own src).

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, XCircle, Info, X, Circle, type LucideIcon } from 'lucide-react';

// ── Status pill ──────────────────────────────────────────────────────────────
// Matches the operator app's pill: icon + label (status never relies on color
// alone), translucent tone background + ring.
type Tone = 'ok' | 'warning' | 'critical' | 'info' | 'muted';
const TONE: Record<Tone, string> = {
  ok: 'text-ok bg-ok/15 ring-ok/30',
  warning: 'text-warning bg-warning/15 ring-warning/30',
  critical: 'text-critical bg-critical/15 ring-critical/30',
  info: 'text-primary bg-primary/15 ring-primary/30',
  muted: 'text-muted bg-white/5 ring-white/10',
};
// Per-tone icon glyph, so a control-plane status reads at a glance.
const TONE_ICON: Record<Tone, LucideIcon> = {
  ok: CheckCircle2, warning: AlertTriangle, critical: XCircle, info: Info, muted: Circle,
};
export function statusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (['active', 'ok', 'healthy', 'issued', 'paid'].includes(s)) return 'ok';
  if (['trial', 'provisioning', 'pending', 'draft', 'warning', 'past_due'].includes(s)) return 'warning';
  if (['error', 'down', 'critical', 'cancelled', 'suspended', 'revoked'].includes(s)) return 'critical';
  return 'muted';
}
export function StatusPill({ status, label, tone }: { status: string; label?: string; tone?: Tone }) {
  const t = tone ?? statusTone(status);
  const Icon = TONE_ICON[t];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${TONE[t]}`}>
      <Icon size={12} />
      {label ?? status.replace(/_/g, ' ')}
    </span>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-white/10 ${className}`} />;
}

// ── Empty state ────────────────────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, hint, action }: {
  icon: LucideIcon; title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="grid place-items-center gap-2 py-10 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-white/5 text-muted"><Icon size={22} /></div>
      <div className="text-sm font-medium">{title}</div>
      {hint && <div className="max-w-xs text-xs text-muted">{hint}</div>}
      {action}
    </div>
  );
}

// ── KPI card ─────────────────────────────────────────────────────────────────
// Matches the operator app's KpiStat: glass card with a hover lift, a tone-
// colored indicator chip, a large tabular value, and an optional hint/unit.
const KPI_TONE: Record<Tone, { bg: string; text: string }> = {
  ok: { bg: 'bg-ok/10', text: 'text-ok' },
  warning: { bg: 'bg-warning/10', text: 'text-warning' },
  critical: { bg: 'bg-critical/10', text: 'text-critical' },
  info: { bg: 'bg-primary/10', text: 'text-primary' },
  muted: { bg: 'bg-white/5', text: 'text-muted' },
};
export function KpiStat({ label, value, unit, tone = 'info', hint }: {
  label: string; value: number | string; unit?: string; tone?: Tone; hint?: string;
}) {
  const t = KPI_TONE[tone];
  return (
    <div className="tk-card tk-card-hover">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted">{label}</span>
        <span className={`grid h-7 w-7 place-items-center rounded-md ${t.bg} ${t.text}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="tk-num text-3xl font-semibold">{value}</span>
        {unit && <span className="text-sm text-muted">{unit}</span>}
      </div>
      {hint && <div className="mt-1 text-[11px] text-muted">{hint}</div>}
    </div>
  );
}

// ── Toast queue (global) ──────────────────────────────────────────────────────
type ToastTone = 'ok' | 'warning' | 'critical' | 'info';
interface ToastMsg { id: number; tone: ToastTone; text: string }
interface ToastApi { show: (text: string, tone?: ToastTone) => void; ok: (t: string) => void; error: (t: string) => void; warn: (t: string) => void; }
const ToastCtx = createContext<ToastApi | null>(null);
export const useToast = (): ToastApi => {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
};

const TOAST_ICON = { ok: CheckCircle2, warning: AlertTriangle, critical: XCircle, info: Info };
const TOAST_TONE = { ok: 'text-ok ring-ok/30', warning: 'text-warning ring-warning/30', critical: 'text-critical ring-critical/30', info: 'text-primary ring-primary/30' };
let toastSeq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const show = useCallback((text: string, tone: ToastTone = 'info') => {
    const id = ++toastSeq;
    setToasts((t) => [...t, { id, tone, text }]);
    // critical stays a bit longer so the operator can read it.
    setTimeout(() => dismiss(id), tone === 'critical' ? 6000 : 3500);
  }, [dismiss]);
  const api: ToastApi = {
    show,
    ok: (t) => show(t, 'ok'),
    error: (t) => show(t, 'critical'),
    warn: (t) => show(t, 'warning'),
  };
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex w-80 flex-col gap-2">
        {toasts.map((t) => {
          const Icon = TOAST_ICON[t.tone];
          return (
            <div key={t.id} className={`tk-card flex items-start gap-2 p-3 text-sm shadow-xl ring-1 ${TOAST_TONE[t.tone]}`}>
              <Icon size={16} className="mt-0.5 shrink-0" />
              <span className="flex-1 text-fg">{t.text}</span>
              <button onClick={() => dismiss(t.id)} className="text-muted hover:text-fg"><X size={14} /></button>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}

// ── Typed-key destructive confirm modal ────────────────────────────────────────
// The user must type `confirmKey` exactly to enable the destructive action — used
// for customer deletion (type the tenant name) and other irreversible operations.
export function ConfirmDestructive({
  title, body, confirmKey, confirmLabel, busy, onConfirm, onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmKey: string;        // the exact text the user must type (e.g. tenant name)
  confirmLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const [typed, setTyped] = useState('');
  const armed = typed.trim() === confirmKey.trim();
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4" onClick={onCancel}>
      <div className="tk-card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center gap-2 text-critical">
          <AlertTriangle size={18} />
          <h2 className="text-base font-semibold">{title}</h2>
        </div>
        <div className="mb-4 text-sm text-muted">{body}</div>
        <label className="mb-1 block text-xs text-muted">
          {t('ui.confirm.typePrefix')} <span className="tk-num font-semibold text-fg">{confirmKey}</span> {t('ui.confirm.typeSuffix')}
        </label>
        <input
          className="tk-input mb-4" value={typed} autoFocus
          onChange={(e) => setTyped(e.target.value)}
          placeholder={confirmKey}
        />
        <div className="flex justify-end gap-2">
          <button className="tk-btn-ghost" onClick={onCancel} disabled={busy}>{t('common:actions.cancel')}</button>
          <button
            className="tk-btn rounded-md bg-critical/90 px-3 text-white hover:bg-critical disabled:opacity-40"
            disabled={!armed || busy}
            onClick={onConfirm}
          >
            {busy ? t('ui.confirm.working') : (confirmLabel ?? t('common:actions.delete'))}
          </button>
        </div>
      </div>
    </div>
  );
}
